import { SplitFactory } from '@deal/splitio-javascript-client';
import React, { Children, Component } from 'react';
import hoistNonReactStatic from 'hoist-non-react-statics';

var OverridingExperimentClient = /** @class */ (function () {
    function OverridingExperimentClient(underlyingExperimentClient, config) {
        this.underlyingExperimentClient = underlyingExperimentClient;
        this.forceDefaultTreatments = config.forceDefaultTreatments || false;
        this.treatment = this.treatment.bind(this);
        var experimentOverrides = config.experimentOverrides;
        /**
         * Collect all experiment & variation pairs => [header:v2, phoneQuiz:on]
         */
        var forcedExperimentVariations = experimentOverrides && experimentOverrides.split(',');
        var forcedVariations = {};
        /**
         * Convert array values to a mapped object of type {experiment: variaton}
         * EX: { header: `v2`, phoneQuiz: `on`}
         */
        forcedExperimentVariations &&
            forcedExperimentVariations.forEach(function (forcedExperimentVariation) {
                var _a = forcedExperimentVariation.split(':'), experiment = _a[0], variation = _a[1];
                // Need both, passing a null variation resets the forced override
                if (!experiment || !variation) {
                    console.warn('Incorrectly formatted experiment override passed', forcedExperimentVariation);
                    return false;
                }
                forcedVariations[experiment] = variation;
            });
        this.forcedExperimentVariations = forcedVariations;
    }
    OverridingExperimentClient.prototype.treatment = function (_a) {
        var id = _a.id, experiment = _a.experiment, attributes = _a.attributes, defaultTreatment = _a.defaultTreatment;
        var forcedVariationExperiment = this.forcedExperimentVariations[experiment];
        /**
         * If there is a forced variation for the provided experiment short-circuit underlying call to `treatment()` and provided the forced variation
         */
        if (forcedVariationExperiment) {
            return {
                result: forcedVariationExperiment,
                promise: Promise.resolve(forcedVariationExperiment),
                active: Promise.resolve(true),
                changeNumber: Promise.resolve(-1)
            };
        }
        else if (this.forceDefaultTreatments && defaultTreatment) {
            return {
                result: defaultTreatment,
                promise: Promise.resolve(defaultTreatment),
                active: Promise.resolve(true),
                changeNumber: Promise.resolve(-1)
            };
        }
        else {
            /**
             * Options aren't used in any of the underlyuing treatment methods so we can safely drop them here
             */
            return this.underlyingExperimentClient.treatment({
                id: id,
                experiment: experiment,
                attributes: attributes,
                options: {}
            });
        }
    };
    OverridingExperimentClient.prototype.experiments = function (id) {
        return this.underlyingExperimentClient.experiments(id);
    };
    return OverridingExperimentClient;
}());

var FallbackExperimentClient = /** @class */ (function () {
    function FallbackExperimentClient(underlyingExperimentClient, fallbackTreatments) {
        this.underlyingExperimentClient = underlyingExperimentClient;
        this.fallbackTreatments = fallbackTreatments;
        this.treatment = this.treatment.bind(this);
    }
    FallbackExperimentClient.prototype.treatment = function (_a) {
        var id = _a.id, experiment = _a.experiment, attributes = _a.attributes, defaultTreatment = _a.defaultTreatment;
        var fallbackResult = this.fallbackTreatments[experiment];
        var underlyingTreatment = this.underlyingExperimentClient.treatment({
            id: id,
            experiment: experiment,
            options: {},
            attributes: attributes,
            defaultTreatment: defaultTreatment
        });
        if (underlyingTreatment.result) {
            // When the underlying result is immediately available, use it
            return underlyingTreatment;
        }
        if (fallbackResult) {
            // When the underlying result is not immediately available, but the fallback result is available, use the fallback result
            return {
                result: fallbackResult.treatment,
                promise: Promise.resolve(fallbackResult.treatment),
                active: Promise.resolve(fallbackResult.active),
                changeNumber: Promise.resolve(fallbackResult.changeNumber)
            };
        }
        // Otherwise, wait for the underlying result
        return underlyingTreatment;
    };
    FallbackExperimentClient.prototype.experiments = function (id) {
        return this.underlyingExperimentClient.experiments(id);
    };
    return FallbackExperimentClient;
}());

var TrackingExperimentClient = /** @class */ (function () {
    function TrackingExperimentClient(underlyingExperimentClient, config) {
        this.underlyingExperimentClient = underlyingExperimentClient;
        this.config = config;
        this.tracked = {};
        this.treatment = this.treatment.bind(this);
    }
    TrackingExperimentClient.prototype.treatment = function (_a) {
        var _this = this;
        var id = _a.id, experiment = _a.experiment, attributes = _a.attributes, options = _a.options, defaultTreatment = _a.defaultTreatment;
        /**
         * Although not required, the default treatment must be passed into this first underlying client.
         * If we are using an override client it might use the default treatment
         */
        var treatment = this.underlyingExperimentClient.treatment({
            id: id,
            experiment: experiment,
            attributes: attributes,
            options: options,
            defaultTreatment: defaultTreatment
        });
        /**
         * Don't track if:
         * 1. skipping the experiment
         * 2. explicitely suprressing tracking
         * 3. overriding the experiemnt
         * 4. forcing default treatments (we do this for bots, such as scrapers, synthetic monitoring, and e2e testing)
         */
        if (options.skip ||
            options.suppressTracking ||
            this.config.experimentOverrides ||
            this.config.forceDefaultTreatments) {
            return treatment;
        }
        Promise.all([treatment.promise, treatment.active, treatment.changeNumber]).then(function (_a) {
            var variation = _a[0], active = _a[1], changeNumber = _a[2];
            // Additionally do not track if the experiment is inactive (killed/resolved)
            if (!active) {
                return;
            }
            if (!_this.tracked[id]) {
                _this.tracked[id] = {};
            }
            if (_this.tracked[id][experiment] !== variation) {
                _this.config.onExperimentViewed &&
                    _this.config.onExperimentViewed({
                        experiment: experiment,
                        variation: variation,
                        nonInteraction: 1,
                        changeNumber: changeNumber
                    });
                _this.tracked[id][experiment] = variation;
            }
        });
        return treatment;
    };
    TrackingExperimentClient.prototype.experiments = function (id) {
        return this.underlyingExperimentClient.experiments(id);
    };
    return TrackingExperimentClient;
}());

var SplitIOExperimentClient = /** @class */ (function () {
    function SplitIOExperimentClient(client, manager) {
        // On server-side, the Split IO client is expected to be fully initialized
        this.client = client;
        this.manager = manager;
        this.treatment = this.treatment.bind(this);
    }
    SplitIOExperimentClient.prototype.treatment = function (_a) {
        var id = _a.id, experiment = _a.experiment, attributes = _a.attributes;
        var splitView = this.manager.split(experiment);
        var result = this.client.getTreatment(id, experiment, attributes);
        var active = splitView ? !splitView.killed : true;
        var changeNumber = (splitView && splitView.changeNumber) || -1;
        return {
            result: result,
            promise: Promise.resolve(result),
            active: Promise.resolve(active),
            changeNumber: Promise.resolve(changeNumber)
        };
    };
    SplitIOExperimentClient.prototype.experiments = function (id) {
        return Promise.resolve(this.manager.splits().map(function (split) {
            return {
                name: split.name,
                variations: split.treatments,
                active: split.killed,
                version: split.changeNumber
            };
        }));
    };
    return SplitIOExperimentClient;
}());

var SplitIOExperimentClient$1 = /** @class */ (function () {
    function SplitIOExperimentClient(apiKey) {
        this.apiKey = apiKey;
        this.clients = {};
        this.treatment = this.treatment.bind(this);
        this.createNewClient = this.createNewClient.bind(this);
        this.client = this.client.bind(this);
    }
    SplitIOExperimentClient.prototype.createNewClient = function (id) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            var factory = SplitFactory({
                core: {
                    authorizationKey: _this.apiKey,
                    key: id
                },
                startup: {
                    requestTimeoutBeforeReady: 2.5,
                    retriesOnFailureBeforeReady: 3,
                    readyTimeout: 5
                },
                scheduler: {
                    impressionsRefreshRate: 15
                }
            });
            var newClient = factory.client();
            var newManager = factory.manager();
            newClient.on(newClient.Event.SDK_READY, function () {
                resolve({ client: newClient, manager: newManager });
            });
        });
    };
    SplitIOExperimentClient.prototype.client = function (id) {
        var client = this.clients[id];
        if (!client) {
            var newClientPromise = this.createNewClient(id);
            client = {
                splitioPromise: newClientPromise.then(function (initializedClient) {
                    client.splitio = initializedClient;
                    return initializedClient;
                })
            };
            this.clients[id] = client;
        }
        return client;
    };
    SplitIOExperimentClient.prototype.treatment = function (_a) {
        var id = _a.id, experiment = _a.experiment, attributes = _a.attributes;
        var client = this.client(id);
        if (client.splitio) {
            // Already initialized
            var splitView = client.splitio.manager.split(experiment);
            var result = client.splitio.client.getTreatment(experiment, attributes);
            var active = splitView ? !splitView.killed : true;
            var changeNumber = (splitView && splitView.changeNumber) || -1;
            return {
                result: result,
                promise: Promise.resolve(result),
                active: Promise.resolve(active),
                changeNumber: Promise.resolve(changeNumber)
            };
        }
        // Client not yet fully initialized, need to create a promise
        // and leave result undefined until the promise is resolved
        var resultPromise = client.splitioPromise.then(function (splitio) {
            var splitView = splitio.manager.split(experiment);
            var result = splitio.client.getTreatment(experiment, attributes);
            var active = splitView ? !splitView.killed : true;
            var changeNumber = (splitView && splitView.changeNumber) || -1;
            return { result: result, active: active, changeNumber: changeNumber };
        });
        var treatment = {
            promise: resultPromise.then(function (_a) {
                var result = _a.result;
                return result;
            }),
            active: resultPromise.then(function (_a) {
                var active = _a.active;
                return active;
            }),
            changeNumber: resultPromise.then(function (_a) {
                var changeNumber = _a.changeNumber;
                return changeNumber;
            })
        };
        return treatment;
    };
    SplitIOExperimentClient.prototype.experiments = function (id) {
        return this.client(id).splitioPromise.then(function (client) {
            return client.manager.splits().map(function (split) {
                return {
                    name: split.name,
                    variations: split.treatments,
                    active: split.killed,
                    version: split.changeNumber
                };
            });
        });
    };
    return SplitIOExperimentClient;
}());

function createServerExperimentClient(config) {
    var client;
    if (!config.splitioConfig) {
        throw 'Split IO configuration is not set';
    }
    else {
        client = new SplitIOExperimentClient(config.splitioConfig.splitio, config.splitioConfig.manager);
        if (config.forceDefaultTreatments || config.experimentOverrides) {
            client = new OverridingExperimentClient(client, config);
        }
    }
    return client;
}
function createBrowserExperimentClient(config) {
    var client;
    if (!config.splitioConfig) {
        throw 'Split IO configuration is not set';
    }
    else {
        client = new SplitIOExperimentClient$1(config.splitioConfig.apiKey);
        if (config.fallback) {
            client = new FallbackExperimentClient(client, config.fallback);
        }
        if (config.forceDefaultTreatments || config.experimentOverrides) {
            client = new OverridingExperimentClient(client, config);
        }
        if (config.onExperimentViewed) {
            client = new TrackingExperimentClient(client, config);
        }
    }
    return client;
}

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */
/* global Reflect, Promise */

var extendStatics = Object.setPrototypeOf ||
    ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
    function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };

function __extends(d, b) {
    extendStatics(d, b);
    function __() { this.constructor = d; }
    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
}

var __assign = Object.assign || function __assign(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
        s = arguments[i];
        for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
};

var ExperimentContext = React.createContext({
    treatment: function () { return ({
        treatment: 'control',
        promise: Promise.resolve('control'),
        active: Promise.resolve(true),
        changeNumber: Promise.resolve(-1)
    }); },
    experiments: function () { return Promise.resolve([]); }
});
var ExperimentProviderConsumer = ExperimentContext.Consumer;
var ExperimentClientProvider = function (_a) {
    var children = _a.children, experimentClient = _a.experimentClient, id = _a.id, attributes = _a.attributes, defaultOptions = _a.options;
    var identifiedExperimentClient = {
        treatment: function (_a) {
            var experiment = _a.experiment, options = _a.options, defaultTreatment = _a.defaultTreatment;
            return experimentClient.treatment({
                id: id,
                experiment: experiment,
                attributes: attributes,
                defaultTreatment: defaultTreatment,
                options: __assign({}, defaultOptions, options)
            });
        },
        experiments: function () {
            return experimentClient.experiments(id);
        }
    };
    return (React.createElement(ExperimentContext.Provider, { value: identifiedExperimentClient }, Children.only(children)));
};

function getDisplayName(WrappedComponent) {
    return WrappedComponent.displayName || WrappedComponent.name || 'Component';
}
function withExperimentClient(WrappedComponent) {
    var WithExperimentClient = /** @class */ (function (_super) {
        __extends(WithExperimentClient, _super);
        function WithExperimentClient() {
            return _super !== null && _super.apply(this, arguments) || this;
        }
        WithExperimentClient.prototype.render = function () {
            var _this = this;
            return (React.createElement(ExperimentProviderConsumer, null, function (client) { return React.createElement(WrappedComponent, __assign({ identifiedExperimentClient: client }, _this.props)); }));
        };
        WithExperimentClient.displayName = "WithExperimentClient(" + getDisplayName(WrappedComponent) + ")";
        return WithExperimentClient;
    }(Component));
    return hoistNonReactStatic(WithExperimentClient, WrappedComponent);
}

var Experiment = /** @class */ (function (_super) {
    __extends(Experiment, _super);
    function Experiment() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    Experiment.prototype.componentWillMount = function () {
        this.computeVariant(this.props);
    };
    Experiment.prototype.componentWillReceiveProps = function (nextProps) {
        var _a = this.props, id = _a.id, options = _a.options, identifiedExperimentClient = _a.identifiedExperimentClient;
        if (id !== nextProps.id ||
            identifiedExperimentClient !== nextProps.identifiedExperimentClient ||
            options !== nextProps.options) {
            this.computeVariant(nextProps);
        }
    };
    Experiment.prototype.computeVariant = function (props) {
        var _this = this;
        var experiment = props.id, defaultTreatment = props.default, identifiedExperimentClient = props.identifiedExperimentClient, options = props.options;
        var treatment = identifiedExperimentClient.treatment({
            experiment: experiment,
            defaultTreatment: defaultTreatment,
            options: options
        });
        if (treatment.result) {
            this.setState({
                result: treatment.result,
                loading: false
            });
        }
        else {
            // the client is not ready yet
            this.setState({ loading: true, result: null });
            treatment.promise.then(function (result) { return _this.setState({ result: result, loading: false }); });
        }
    };
    Experiment.prototype.render = function () {
        var _a = this.props, children = _a.children, options = _a.options, defaultTreatment = _a.default;
        var _b = this.state, result = _b.result, loading = _b.loading;
        if (typeof children === 'function') {
            return children({
                result: result,
                loading: loading
            });
        }
        if (loading) {
            return null;
        }
        /**
         * TODO: We should expose all Split treatments from `treatment()` instead of just the result so that we can check that all variations actually exist in Split
         * This would give us runtime safety during development that we didn't misspell a treatment name
         */
        var childrenAsArray = React.Children.toArray(children);
        var bucketedVariation = childrenAsArray.find(function (child) {
            return child.props &&
                child.props.id === result;
        });
        var defaultVariation = childrenAsArray.find(function (child) {
            return child.props &&
                child.props.id === defaultTreatment;
        });
        if (!defaultVariation) {
            throw new Error("No default variation found for experiment " + this.props.id);
        }
        if (options && options.skip) {
            return defaultVariation;
        }
        // The selected variation is the variation to render (the control, a treatment, or the default)
        var selectedVariation = bucketedVariation || defaultVariation;
        return selectedVariation;
    };
    return Experiment;
}(Component));
var Experiment$1 = withExperimentClient(Experiment);

var Variation = function (_a) {
    var children = _a.children, id = _a.id;
    if (process.env.NODE_ENV !== 'production') {
        // list from split.io
        var INVALID_IDS = [
            'control',
            'split',
            'datetime',
            'number',
            'string',
            'set',
            'start',
            'starts',
            'contain',
            'contains',
            'between',
            'match',
            'matches',
            'true',
            'false',
            'end',
            'ends',
            'in',
            'not',
            'if',
            'and',
            'is',
            'with',
            'then',
            'does',
            'have',
            'has'
        ];
        if (INVALID_IDS.includes(id)) {
            console.error("Please rename your treatment. The name cannot be " + id + ".");
        }
    }
    return children ? React.createElement(React.Fragment, null, children) : null;
};

export { createServerExperimentClient, createBrowserExperimentClient, Experiment$1 as Experiment, Variation, withExperimentClient, ExperimentClientProvider, ExperimentProviderConsumer };
//# sourceMappingURL=browser.esm.js.map
